// Stage 73.20di — Asset Type Master + Document Type Master admin page.
//
// One combined page with two tabs (Asset Types / Document Types) so admins
// don't have to hop between sidebar entries. Both masters share the same
// CRUD pattern.
//
// Route: /masters/asset-document-types
// RBAC: tenant_admin, super_admin, hr_admin, payroll_admin

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MainLayout from '../components/MainLayout';
import { Plus, Save, X, Package, FileText, ToggleLeft, ToggleRight, Shield, Link2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EMPTY_ASSET = () => ({
  company_id: '', name: '', category: '', description: '',
  required_document: false, serial_number_required: false,
  returnable: true, active: true,
});
const EMPTY_DOC = () => ({
  company_id: '', name: '', code: '',
  is_mandatory: false, is_asset_related: false, active: true,
  applicable_categories: [],
});

export default function AssetDocumentTypeMasters() {
  const [tab, setTab] = useState('asset');                        // 'asset' | 'document'
  const [companies, setCompanies] = useState([]);
  const [selCompany, setSelCompany] = useState('');
  const [assetTypes, setAssetTypes] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_ASSET());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const c = await axios.get(`${API_URL}/companies`, { withCredentials: true });
        setCompanies(c.data || []);
        if ((c.data || []).length) setSelCompany(c.data[0].id);
      } catch { toast.error('Failed to load companies'); }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'asset') {
        const { data } = await axios.get(`${API_URL}/masters/asset-types?company_id=${selCompany}`, { withCredentials: true });
        setAssetTypes(data || []);
      } else {
        const { data } = await axios.get(`${API_URL}/masters/document-types?company_id=${selCompany}`, { withCredentials: true });
        setDocTypes(data || []);
      }
    } catch { toast.error('Load failed'); }
    setLoading(false);
  }, [selCompany, tab]);

  useEffect(() => { if (selCompany) load(); }, [selCompany, tab, load]);


  const openCreate = () => {
    setEditingId(null);
    setForm(tab === 'asset' ? { ...EMPTY_ASSET(), company_id: selCompany } : { ...EMPTY_DOC(), company_id: selCompany });
    setShowForm(true);
  };
  const openEdit = (row) => {
    setEditingId(row.id);
    if (tab === 'asset') {
      setForm({
        company_id: row.company_id, name: row.name || '', category: row.category || '',
        description: row.description || '',
        required_document: !!row.required_document,
        serial_number_required: !!row.serial_number_required,
        returnable: row.returnable !== false, active: row.active !== false,
      });
    } else {
      setForm({
        company_id: row.company_id, name: row.name || '', code: row.code || '',
        is_mandatory: !!row.is_mandatory, is_asset_related: !!row.is_asset_related,
        active: row.active !== false,
        applicable_categories: row.applicable_categories || [],
      });
    }
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditingId(null); };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      const url = tab === 'asset'
        ? `${API_URL}/masters/asset-types${editingId ? '/' + editingId : ''}`
        : `${API_URL}/masters/document-types${editingId ? '/' + editingId : ''}`;
      const method = editingId ? 'put' : 'post';
      await axios[method](url, form, { withCredentials: true });
      toast.success(editingId ? 'Updated' : 'Created');
      closeForm();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const deactivate = async (row) => {
    if (!window.confirm(`Deactivate "${row.name}"? Old records referencing it will remain visible.`)) return;
    try {
      const url = tab === 'asset'
        ? `${API_URL}/masters/asset-types/${row.id}`
        : `${API_URL}/masters/document-types/${row.id}`;
      await axios.delete(url, { withCredentials: true });
      toast.success('Deactivated');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const rows = tab === 'asset' ? assetTypes : docTypes;

  return (
    <MainLayout>
      <div className="p-6" data-testid="asset-doc-type-masters-page">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Asset & Document Type Masters</h1>
            <p className="text-sm text-slate-500 mt-1">
              Configure the dropdown values that appear on the Employee Assets & Documents page.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              data-testid="master-company-select"
              value={selCompany}
              onChange={(e) => setSelCompany(e.target.value)}
              className="border border-slate-300 rounded px-3 py-1.5 text-sm"
            >
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button
              data-testid="master-add-btn"
              onClick={openCreate}
              disabled={!selCompany}
              className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg px-3.5 py-2 text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> New {tab === 'asset' ? 'Asset Type' : 'Document Type'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center border-b border-slate-200 mb-4">
          <button
            data-testid="tab-asset-types"
            onClick={() => setTab('asset')}
            className={`inline-flex items-center gap-1.5 px-4 py-2 border-b-2 text-sm font-medium ${
              tab === 'asset' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Package className="w-4 h-4" /> Asset Types
          </button>
          <button
            data-testid="tab-document-types"
            onClick={() => setTab('document')}
            className={`inline-flex items-center gap-1.5 px-4 py-2 border-b-2 text-sm font-medium ${
              tab === 'document' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <FileText className="w-4 h-4" /> Document Types
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="master-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-xs text-slate-600">
              {tab === 'asset' ? (
                <tr>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Category</th>
                  <th className="text-left px-4 py-2">Returnable</th>
                  <th className="text-left px-4 py-2">Serial #</th>
                  <th className="text-left px-4 py-2">Doc Req.</th>
                  <th className="text-left px-4 py-2">Active</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              ) : (
                <tr>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Code</th>
                  <th className="text-left px-4 py-2">Mandatory</th>
                  <th className="text-left px-4 py-2">Asset-related</th>
                  <th className="text-left px-4 py-2">Active</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              )}
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="p-6 text-center text-slate-500">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-slate-400">
                  No {tab === 'asset' ? 'asset' : 'document'} types configured. Click <b>New</b> to add one.
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100" data-testid={`master-row-${r.id}`}>
                  {tab === 'asset' ? (
                    <>
                      <td className="px-4 py-2 text-slate-900">{r.name}</td>
                      <td className="px-4 py-2 text-slate-700">{r.category || '—'}</td>
                      <td className="px-4 py-2">{r.returnable ? <ToggleRight className="w-4 h-4 text-emerald-600" /> : <ToggleLeft className="w-4 h-4 text-slate-400" />}</td>
                      <td className="px-4 py-2">{r.serial_number_required ? <ToggleRight className="w-4 h-4 text-emerald-600" /> : <ToggleLeft className="w-4 h-4 text-slate-400" />}</td>
                      <td className="px-4 py-2">{r.required_document ? <ToggleRight className="w-4 h-4 text-emerald-600" /> : <ToggleLeft className="w-4 h-4 text-slate-400" />}</td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-2 text-slate-900">{r.name}</td>
                      <td className="px-4 py-2 text-slate-700 font-mono">{r.code || '—'}</td>
                      <td className="px-4 py-2">
                        {r.is_mandatory
                          ? <Shield className="w-4 h-4 text-red-600" title="Mandatory" />
                          : <Shield className="w-4 h-4 text-slate-300" />}
                      </td>
                      <td className="px-4 py-2">
                        {r.is_asset_related
                          ? <Link2 className="w-4 h-4 text-indigo-600" title="Asset-related" />
                          : <Link2 className="w-4 h-4 text-slate-300" />}
                      </td>
                    </>
                  )}
                  <td className="px-4 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${r.active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {r.active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button data-testid={`master-edit-${r.id}`} onClick={() => openEdit(r)} className="text-blue-600 hover:underline text-xs mr-3">Edit</button>
                    {r.active !== false && (
                      <button data-testid={`master-deactivate-${r.id}`} onClick={() => deactivate(r)} className="text-red-600 hover:underline text-xs">Deactivate</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Modal — Create / Edit */}
        {showForm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="master-form-modal">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">
                  {editingId ? 'Edit' : 'New'} {tab === 'asset' ? 'Asset Type' : 'Document Type'}
                </h2>
                <button onClick={closeForm} className="text-slate-500 hover:text-slate-700"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-4 space-y-3">
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Name *</span>
                  <input
                    data-testid="master-form-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                    placeholder={tab === 'asset' ? 'e.g. Laptop' : 'e.g. Aadhaar Card'}
                  />
                </label>
                {tab === 'asset' ? (
                  <>
                    <label className="block">
                      <span className="text-xs font-medium text-slate-600">Category</span>
                      <input
                        data-testid="master-form-category"
                        value={form.category}
                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                        className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                        placeholder="e.g. IT, Facility, Safety"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs font-medium text-slate-600">Description</span>
                      <textarea
                        data-testid="master-form-description"
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        rows={2}
                        className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                      />
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="inline-flex items-center gap-2 text-sm">
                        <input
                          data-testid="master-form-returnable"
                          type="checkbox" checked={!!form.returnable}
                          onChange={(e) => setForm({ ...form, returnable: e.target.checked })}
                        />
                        Returnable
                      </label>
                      <label className="inline-flex items-center gap-2 text-sm">
                        <input
                          data-testid="master-form-serial-required"
                          type="checkbox" checked={!!form.serial_number_required}
                          onChange={(e) => setForm({ ...form, serial_number_required: e.target.checked })}
                        />
                        Serial # required
                      </label>
                      <label className="inline-flex items-center gap-2 text-sm">
                        <input
                          data-testid="master-form-required-document"
                          type="checkbox" checked={!!form.required_document}
                          onChange={(e) => setForm({ ...form, required_document: e.target.checked })}
                        />
                        Handover doc required
                      </label>
                      <label className="inline-flex items-center gap-2 text-sm">
                        <input
                          data-testid="master-form-active"
                          type="checkbox" checked={form.active !== false}
                          onChange={(e) => setForm({ ...form, active: e.target.checked })}
                        />
                        Active
                      </label>
                    </div>
                  </>
                ) : (
                  <>
                    <label className="block">
                      <span className="text-xs font-medium text-slate-600">Code</span>
                      <input
                        data-testid="master-form-code"
                        value={form.code}
                        onChange={(e) => setForm({ ...form, code: e.target.value })}
                        className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm font-mono"
                        placeholder="e.g. AADHAAR"
                      />
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="inline-flex items-center gap-2 text-sm">
                        <input
                          data-testid="master-form-mandatory"
                          type="checkbox" checked={!!form.is_mandatory}
                          onChange={(e) => setForm({ ...form, is_mandatory: e.target.checked })}
                        />
                        Mandatory
                      </label>
                      <label className="inline-flex items-center gap-2 text-sm">
                        <input
                          data-testid="master-form-asset-related"
                          type="checkbox" checked={!!form.is_asset_related}
                          onChange={(e) => setForm({ ...form, is_asset_related: e.target.checked })}
                        />
                        Asset-related
                      </label>
                      <label className="inline-flex items-center gap-2 text-sm">
                        <input
                          data-testid="master-form-active"
                          type="checkbox" checked={form.active !== false}
                          onChange={(e) => setForm({ ...form, active: e.target.checked })}
                        />
                        Active
                      </label>
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-200 bg-slate-50">
                <button data-testid="master-form-cancel" onClick={closeForm}
                        className="px-3 py-1.5 rounded text-sm text-slate-700 hover:bg-slate-200">Cancel</button>
                <button data-testid="master-form-save" disabled={saving} onClick={save}
                        className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded px-4 py-1.5 text-sm font-medium">
                  <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
